import React, { useEffect, useRef, useCallback } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { Box, Typography, Card, CardContent, CardMedia, useTheme, useMediaQuery } from '@mui/material';

const KeenSliderCarousel = ({ mediaItems }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const timeoutRef = useRef(null);
  const mouseOverRef = useRef(false);
  const sliderInstanceRef = useRef(null);
  
  // Detect mobile device for video policy compliance
  const isMobileDevice = useRef(
    typeof navigator !== 'undefined' && 
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  ).current;
  
  // Calculate slides per view based on screen size
  const getSlidesPerView = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (isDesktop) return 3;
    return 2; // Default for medium screens
  };

  // Safe slider operations with error handling
  const safeSliderNext = useCallback(() => {
    try {
      if (sliderInstanceRef.current && typeof sliderInstanceRef.current.next === 'function') {
        sliderInstanceRef.current.next();
      }
    } catch (error) {
      console.warn('Error during slider navigation:', error);
    }
  }, []);

  const clearNextTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const nextTimeout = useCallback(() => {
    clearNextTimeout();
    if (mouseOverRef.current) return;
    
    timeoutRef.current = setTimeout(() => {
      safeSliderNext();
    }, 3000);
  }, [clearNextTimeout, safeSliderNext]);

  const [sliderRef] = useKeenSlider({
    loop: true,
    slides: {
      perView: getSlidesPerView(),
      spacing: isMobile ? 8 : 16,
    },
    created(slider) {
      try {
        sliderInstanceRef.current = slider;
        
        // Add event listeners with error handling
        const handleMouseOver = () => {
          mouseOverRef.current = true;
          clearNextTimeout();
        };
        
        const handleMouseOut = () => {
          mouseOverRef.current = false;
          nextTimeout();
        };

        if (slider.container) {
          slider.container.addEventListener("mouseover", handleMouseOver);
          slider.container.addEventListener("mouseout", handleMouseOut);
        }

        // Store event listeners for cleanup
        slider._customEventListeners = {
          mouseover: handleMouseOver,
          mouseout: handleMouseOut
        };

        nextTimeout();
      } catch (error) {
        console.warn('Error during slider creation:', error);
      }
    },
    dragStarted() {
      clearNextTimeout();
    },
    animationEnded() {
      nextTimeout();
    },
    updated() {
      nextTimeout();
    }
  });

  // Cleanup effect
  useEffect(() => {
    return () => {
      try {
        clearNextTimeout();
        
        // Clean up event listeners
        if (sliderInstanceRef.current && sliderInstanceRef.current.container) {
          const slider = sliderInstanceRef.current;
          if (slider._customEventListeners) {
            slider.container.removeEventListener("mouseover", slider._customEventListeners.mouseover);
            slider.container.removeEventListener("mouseout", slider._customEventListeners.mouseout);
          }
        }
        
        // Clear slider reference
        sliderInstanceRef.current = null;
      } catch (error) {
        console.warn('Error during cleanup:', error);
      }
    };
  }, [clearNextTimeout]);

  const renderMediaItem = (item, index) => {
    try {
      if (!item) {
        console.warn(`Carousel item at index ${index} is null or undefined`);
        return null;
      }

      return (
        <Card
          key={index}
          sx={{
            height: { xs: 300, sm: 400, md: 600 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxShadow: theme.shadows?.[3] || '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: theme.shape?.borderRadius || 1,
            overflow: 'hidden',
          }}
        >
          {item.type === 'image' && item.src && (
            <CardMedia
              component="img"
              image={item.src}
              alt={item.alt || `Image ${index + 1}`}
              sx={{
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                console.warn(`Failed to load image: ${item.src}`);
                e.target.style.display = 'none';
              }}
            />
          )}
          {item.type === 'video' && item.src && (
            <CardMedia
              component="video"
              src={item.src}
              autoPlay={true}
              loop={true}
              muted
              playsInline
              controls={isMobileDevice}
              poster={item.poster} // Only use explicit poster, no auto-generation
              preload={isMobileDevice ? 'metadata' : 'auto'}
              sx={{
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                console.warn(`Failed to load video: ${item.src}`);
                // Don't hide video, let it show browser's default player
              }}
            />
          )}
          {item.type === 'marketing' && (
            <CardContent
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                bgcolor: theme.palette?.gold?.main || '#FAE03F',
                color: theme.palette?.gold?.contrastText || '#000',
                p: theme.spacing?.(3) || 3,
              }}
            >
              <Typography variant="h2" component="div" sx={{ mb: 2, lineHeight: 1 }}>
                {item.title || 'Marketing Content'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {item.description || ''}
              </Typography>
            </CardContent>
          )}
        </Card>
      );
    } catch (error) {
      console.warn(`Error rendering carousel item at index ${index}:`, error);
      return (
        <Card
          key={index}
          sx={{
            height: { xs: 300, sm: 400, md: 600 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: theme.shadows?.[3] || '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: theme.shape?.borderRadius || 1,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Content unavailable
          </Typography>
        </Card>
      );
    }
  };

  return (
    <Box ref={sliderRef} className="keen-slider">
      {Array.isArray(mediaItems) ? mediaItems.map((item, index) => (
        <Box key={index} className="keen-slider__slide">
          {renderMediaItem(item, index)}
        </Box>
      )) : (
        <Box className="keen-slider__slide">
          <Card sx={{ 
            height: { xs: 300, sm: 400, md: 600 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            boxShadow: theme.shadows?.[3] || '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: theme.shape?.borderRadius || 1,
          }}>
            <Typography variant="body2" color="text.secondary">
              No media items available
            </Typography>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default KeenSliderCarousel;